import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { useToast } from '../../hooks/use-toast';
import { vocabularyAPI, progressAPI } from '../../services/api';
import { 
  BookOpen, 
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  XCircle,
  Trophy,
  Lightbulb,
  Keyboard,
  MousePointer,
  RotateCcw,
  Volume2
} from 'lucide-react';

const PossessivesGame = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [gameMode, setGameMode] = useState(null); // 'multiple-choice' or 'type-it'
  const [vocabulary, setVocabulary] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(10);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [typedAnswer, setTypedAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [streak, setStreak] = useState(0);

  // Possessive endings configuration
  const possessiveEndings = {
    singular: [
      { pronoun: 'my', arabic: 'ana', directEnding: 'i', directEndingVowel: 'ya', indirectEnding: 'i' },
      { pronoun: 'your', arabic: 'nta/nti', directEnding: 'ek', directEndingVowel: 'k', indirectEnding: 'ek' },
      { pronoun: 'his', arabic: 'huwa', directEnding: 'ou', directEndingVowel: 'h', indirectEnding: 'ou' },
      { pronoun: 'her', arabic: 'hiya', directEnding: 'ha', directEndingVowel: 'ha', indirectEnding: 'ha' },
    ],
    plural: [
      { pronoun: 'our', arabic: 'hna', directEnding: 'na', directEndingVowel: 'na', indirectEnding: 'na' },
      { pronoun: 'your (pl.)', arabic: 'ntouma', directEnding: 'koum', directEndingVowel: 'koum', indirectEnding: 'koum' },
      { pronoun: 'their', arabic: 'houma', directEnding: 'houm', directEndingVowel: 'houm', indirectEnding: 'houm' },
    ]
  };

  const allPronouns = [...possessiveEndings.singular, ...possessiveEndings.plural];

  // Helper to get indirect pattern - randomly choose diyal or ta3
  const getIndirectPattern = (pronoun) => {
    const prefix = Math.random() > 0.5 ? 'diyal' : 'ta3';
    return prefix + pronoun.indirectEnding;
  };

  // Load vocabulary with possessive types
  useEffect(() => {
    loadVocabulary();
  }, []);

  const loadVocabulary = async () => {
    try {
      setLoading(true);
      const response = await vocabularyAPI.getAll({ limit: 500 });
      
      // Filter only nouns with possessive type set
      const nounsWithPossessive = (response.words || []).filter(
        word => word.wordType === 'noun' && word.possessiveType && word.possessiveType !== 'none'
      );
      
      if (nounsWithPossessive.length < 5) {
        // If not enough tagged nouns, use all nouns as fallback with 'both' type
        const allNouns = (response.words || []).filter(word => word.wordType === 'noun');
        if (allNouns.length > 0) {
          setVocabulary(allNouns.map(n => ({ ...n, possessiveType: n.possessiveType || 'both' })));
        } else {
          // Last fallback: use any words
          setVocabulary((response.words || []).slice(0, 20).map(w => ({ ...w, possessiveType: 'both' })));
        }
      } else {
        setVocabulary(nounsWithPossessive);
      }
    } catch (error) {
      toast({
        title: "Error loading vocabulary",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to apply ta marbuta rule
  // When a noun ends in 'a', the 'a' changes to 't' before adding possessive ending
  const applyTaMarbutaRule = (noun) => {
    if (noun.toLowerCase().endsWith('a')) {
      return noun.slice(0, -1) + 't';
    }
    return noun;
  };

  // Special mum/dad words that have unique rules
  const specialMumDadWords = ['mama', 'papa', 'yima', 'beba', 'baba'];
  
  // Check if word is a special mum/dad word
  const isSpecialMumDadWord = (word) => {
    return specialMumDadWords.includes(word.toLowerCase());
  };

  // Apply special mum/dad transformation
  // yima -> yime-, beba -> bebe- (change final 'a' to 'e')
  const applyMumDadRule = (noun) => {
    const lowerNoun = noun.toLowerCase();
    if (lowerNoun === 'yima') {
      return 'yime';
    } else if (lowerNoun === 'beba') {
      return 'bebe';
    }
    return noun;
  };

  // Generate a question
  const generateQuestion = useCallback(() => {
    if (vocabulary.length === 0) return null;

    let word = vocabulary[Math.floor(Math.random() * vocabulary.length)];
    let pronoun = allPronouns[Math.floor(Math.random() * allPronouns.length)];
    
    // Special handling for mum/dad words
    const isSpecialWord = isSpecialMumDadWord(word.algerianWord);
    
    // If it's a special mum/dad word and pronoun is "my", skip it
    // because mama/papa/yima/beba/baba already mean "my mum/dad"
    if (isSpecialWord && pronoun.pronoun === 'my') {
      // Pick a different pronoun (not "my")
      const otherPronouns = allPronouns.filter(p => p.pronoun !== 'my');
      pronoun = otherPronouns[Math.floor(Math.random() * otherPronouns.length)];
    }
    
    // For mama, papa, baba - these can only be "my", so skip them entirely
    const wordLower = word.algerianWord.toLowerCase();
    if (wordLower === 'mama' || wordLower === 'papa' || wordLower === 'baba') {
      // Find a different word that's not these special ones
      const otherWords = vocabulary.filter(w => 
        !['mama', 'papa', 'baba'].includes(w.algerianWord.toLowerCase())
      );
      if (otherWords.length > 0) {
        word = otherWords[Math.floor(Math.random() * otherWords.length)];
      }
    }
    
    // Determine the correct answer based on possessive type
    let correctAnswer;
    let questionType;
    
    // Re-check if the new word is special (yima or beba)
    const isYimaOrBeba = ['yima', 'beba'].includes(word.algerianWord.toLowerCase());
    
    // Check if word ends in 'a' (ta marbuta) - but NOT for special yima/beba words
    const endsInTaMarbuta = word.algerianWord.toLowerCase().endsWith('a') && !isYimaOrBeba;
    
    if (word.possessiveType === 'direct') {
      if (isYimaOrBeba) {
        // Special mum/dad rule: yima -> yime-, beba -> bebe-
        const stem = applyMumDadRule(word.algerianWord);
        correctAnswer = stem + pronoun.directEnding;
      } else if (endsInTaMarbuta) {
        // Apply ta marbuta rule: remove 'a', add 't', then add ending
        const stem = applyTaMarbutaRule(word.algerianWord);
        correctAnswer = stem + pronoun.directEnding;
      } else {
        // Check if word ends in other vowel (for -ya/-k/-h variants)
        const endsInVowel = /[eiou]$/i.test(word.algerianWord);
        const ending = endsInVowel ? pronoun.directEndingVowel : pronoun.directEnding;
        correctAnswer = word.algerianWord + ending;
      }
      questionType = 'direct';
    } else if (word.possessiveType === 'indirect') {
      correctAnswer = word.algerianWord + ' ' + getIndirectPattern(pronoun);
      questionType = 'indirect';
    } else {
      // 'both' - randomly choose which pattern to test
      if (Math.random() > 0.5) {
        if (isYimaOrBeba) {
          const stem = applyMumDadRule(word.algerianWord);
          correctAnswer = stem + pronoun.directEnding;
        } else if (endsInTaMarbuta) {
          const stem = applyTaMarbutaRule(word.algerianWord);
          correctAnswer = stem + pronoun.directEnding;
        } else {
          const endsInVowel = /[eiou]$/i.test(word.algerianWord);
          const ending = endsInVowel ? pronoun.directEndingVowel : pronoun.directEnding;
          correctAnswer = word.algerianWord + ending;
        }
        questionType = 'direct';
      } else {
        correctAnswer = word.algerianWord + ' ' + getIndirectPattern(pronoun);
        questionType = 'indirect';
      }
    }

    // Generate wrong answers for multiple choice
    const wrongAnswers = [];
    const usedAnswers = new Set([correctAnswer.toLowerCase()]);
    
    // Generate plausible wrong answers
    // For mum/dad words, exclude "my" pronoun from wrong answers
    let otherPronouns = allPronouns.filter(p => p.pronoun !== pronoun.pronoun);
    if (isYimaOrBeba) {
      otherPronouns = otherPronouns.filter(p => p.pronoun !== 'my');
    }
    
    while (wrongAnswers.length < 3 && otherPronouns.length > 0) {
      const wrongPronoun = otherPronouns[Math.floor(Math.random() * otherPronouns.length)];
      let wrongAnswer;
      
      if (questionType === 'direct') {
        if (isYimaOrBeba) {
          // Apply mum/dad rule for wrong answers too
          const stem = applyMumDadRule(word.algerianWord);
          wrongAnswer = stem + wrongPronoun.directEnding;
        } else if (endsInTaMarbuta) {
          // Apply ta marbuta rule for wrong answers too
          const stem = applyTaMarbutaRule(word.algerianWord);
          wrongAnswer = stem + wrongPronoun.directEnding;
        } else {
          const endsInVowel = /[eiou]$/i.test(word.algerianWord);
          const ending = endsInVowel ? wrongPronoun.directEndingVowel : wrongPronoun.directEnding;
          wrongAnswer = word.algerianWord + ending;
        }
      } else {
        wrongAnswer = word.algerianWord + ' ' + getIndirectPattern(wrongPronoun);
      }
      
      if (!usedAnswers.has(wrongAnswer.toLowerCase())) {
        wrongAnswers.push(wrongAnswer);
        usedAnswers.add(wrongAnswer.toLowerCase());
      }
      
      // Remove used pronoun to avoid duplicates
      const idx = otherPronouns.indexOf(wrongPronoun);
      if (idx > -1) otherPronouns.splice(idx, 1);
    }

    // Shuffle options
    const options = [correctAnswer, ...wrongAnswers].sort(() => Math.random() - 0.5);

    return {
      word,
      pronoun,
      correctAnswer,
      questionType,
      options
    };
  }, [vocabulary, allPronouns]);

  // Start game with selected mode
  const startGame = (mode) => {
    setGameMode(mode);
    setQuestionIndex(0);
    setScore(0);
    setStreak(0);
    setGameComplete(false);
    setShowResult(false);
    setSelectedAnswer(null);
    setTypedAnswer('');
    
    const question = generateQuestion();
    setCurrentQuestion(question);
  };

  // Check answer
  const checkAnswer = (answer) => {
    if (!currentQuestion || showResult) return;
    
    // Normalize the answer: lowercase, trim, and normalize spaces
    const normalizeAnswer = (str) => str.toLowerCase().trim().replace(/\s+/g, ' ');
    
    const userAnswer = normalizeAnswer(answer);
    const correctAnswer = normalizeAnswer(currentQuestion.correctAnswer);
    
    // Check for exact match
    let correct = userAnswer === correctAnswer;
    
    // If not exact match and it's an indirect question, check for ta3/diyal equivalence
    if (!correct && currentQuestion.questionType === 'indirect') {
      // Create alternative correct answers by swapping diyal <-> ta3
      const alternatives = [];
      
      // If correct answer uses diyal, also accept ta3
      if (correctAnswer.includes('diyal')) {
        alternatives.push(correctAnswer.replace(/diyal/g, 'ta3'));
      }
      // If correct answer uses ta3, also accept diyal
      if (correctAnswer.includes('ta3')) {
        alternatives.push(correctAnswer.replace(/ta3/g, 'diyal'));
      }
      
      // Check if user answer matches any alternative
      correct = alternatives.some(alt => userAnswer === alt);
    }
    
    setIsCorrect(correct);
    setShowResult(true);
    
    if (correct) {
      setScore(score + 1);
      setStreak(streak + 1);
    } else {
      setStreak(0);
    }
  };

  // Handle multiple choice selection
  const handleOptionSelect = (option) => {
    setSelectedAnswer(option);
    checkAnswer(option);
  };

  // Handle typed answer submission
  const handleTypeSubmit = (e) => {
    e.preventDefault();
    if (typedAnswer.trim()) {
      checkAnswer(typedAnswer);
    }
  };

  // Next question
  const nextQuestion = async () => {
    if (questionIndex + 1 >= totalQuestions) {
      setGameComplete(true);
      
      // Save progress
      try {
        await progressAPI.update({
          category: 'possessives',
          score: score,
          totalQuestions: totalQuestions,
          gameType: 'possessives'
        });
      } catch (error) {
        console.error('Failed to save progress:', error);
      }
    } else {
      setQuestionIndex(questionIndex + 1);
      setShowResult(false);
      setSelectedAnswer(null);
      setTypedAnswer('');
      setCurrentQuestion(generateQuestion());
    }
  };

  // Reset game
  const resetGame = () => {
    setGameMode(null);
    setGameComplete(false);
    setShowResult(false);
    setSelectedAnswer(null);
    setTypedAnswer('');
    setScore(0);
    setStreak(0);
    setQuestionIndex(0);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading game...</p>
        </div>
      </div>
    );
  }

  // Mode Selection Screen
  if (!gameMode) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full mb-4">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Possessives Game</h1>
          <p className="text-gray-600">Practice forming possessive phrases in Algerian Arabic</p>
        </div>

        {/* Learn First Link */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-center">
          <p className="text-blue-800 mb-2">New to possessives?</p>
          <Link to="/learn/possessives">
            <Button variant="outline" className="flex items-center gap-2 mx-auto">
              <BookOpen className="w-4 h-4" />
              Learn the patterns first
            </Button>
          </Link>
        </div>

        {/* Mode Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all hover:border-green-500 border-2"
            onClick={() => startGame('multiple-choice')}
          >
            <CardContent className="p-6 text-center">
              <MousePointer className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Multiple Choice</h3>
              <p className="text-gray-600 text-sm">
                Select the correct possessive form from 4 options. Great for beginners!
              </p>
              <Badge className="mt-4 bg-green-100 text-green-800">Easier</Badge>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all hover:border-purple-500 border-2"
            onClick={() => startGame('type-it')}
          >
            <CardContent className="p-6 text-center">
              <Keyboard className="w-12 h-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Type It</h3>
              <p className="text-gray-600 text-sm">
                Type the correct possessive form yourself. Test your recall!
              </p>
              <Badge className="mt-4 bg-purple-100 text-purple-800">Harder</Badge>
            </CardContent>
          </Card>
        </div>

        {/* Back to Games */}
        <div className="mt-8 text-center">
          <Link to="/games">
            <Button variant="ghost" className="flex items-center gap-2 mx-auto">
              <ArrowLeft className="w-4 h-4" />
              Back to Games
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Game Complete Screen
  if (gameComplete) {
    const percentage = Math.round((score / totalQuestions) * 100);
    
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card className="text-center">
          <CardContent className="p-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full mb-6">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Game Complete!</h2>
            
            <div className="text-6xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent my-6">
              {score}/{totalQuestions}
            </div>
            
            <p className="text-xl text-gray-600 mb-6">
              {percentage >= 90 ? "Outstanding! You're a possessives master! 🌟" :
               percentage >= 70 ? "Great job! Keep practicing! 👏" :
               percentage >= 50 ? "Good effort! Review the lessons and try again! 💪" :
               "Keep learning! Check out the lessons first. 📚"}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={resetGame} variant="outline" className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4" />
                Play Again
              </Button>
              <Link to="/learn/possessives">
                <Button variant="outline" className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Review Lessons
                </Button>
              </Link>
              <Link to="/games">
                <Button className="flex items-center gap-2">
                  More Games
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Active Game Screen
  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      {/* Progress Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">Question {questionIndex + 1} of {totalQuestions}</span>
          <div className="flex items-center gap-4">
            {streak >= 3 && (
              <Badge className="bg-orange-100 text-orange-800">
                🔥 {streak} streak!
              </Badge>
            )}
            <span className="font-semibold text-green-600">Score: {score}</span>
          </div>
        </div>
        <Progress value={((questionIndex + 1) / totalQuestions) * 100} className="h-2" />
      </div>

      {/* Question Card */}
      {currentQuestion && (
        <Card className="mb-6">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
            <CardTitle className="text-center">
              <span className="text-gray-600 text-lg">Make this possessive:</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {/* Word and Pronoun */}
            <div className="text-center mb-6">
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {currentQuestion.word.algerianWord}
              </div>
              <div className="text-lg text-gray-600 mb-4">
                ({currentQuestion.word.englishTranslation})
              </div>
              <div className="inline-block bg-purple-100 px-4 py-2 rounded-full">
                <span className="text-purple-800 font-semibold">
                  + {currentQuestion.pronoun.pronoun} ({currentQuestion.pronoun.arabic})
                </span>
              </div>
            </div>

            {/* Hint */}
            {currentQuestion.word.possessiveType && (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
                <Lightbulb className="w-4 h-4" />
                <span>
                  {currentQuestion.questionType === 'direct' 
                    ? 'Uses direct endings' 
                    : 'Uses diyal/ta3 pattern'}
                </span>
              </div>
            )}

            {/* Multiple Choice Options */}
            {gameMode === 'multiple-choice' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {currentQuestion.options.map((option, index) => {
                  let buttonClass = "w-full p-4 text-lg font-medium border-2 rounded-lg transition-all ";
                  
                  if (showResult) {
                    if (option.toLowerCase() === currentQuestion.correctAnswer.toLowerCase()) {
                      buttonClass += "border-green-500 bg-green-50 text-green-800";
                    } else if (selectedAnswer === option) {
                      buttonClass += "border-red-500 bg-red-50 text-red-800";
                    } else {
                      buttonClass += "border-gray-200 text-gray-400";
                    }
                  } else {
                    buttonClass += "border-gray-200 hover:border-green-500 hover:bg-green-50";
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleOptionSelect(option)}
                      disabled={showResult}
                      className={buttonClass}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Type It Input */}
            {gameMode === 'type-it' && (
              <form onSubmit={handleTypeSubmit} className="space-y-4">
                <Input
                  type="text"
                  value={typedAnswer}
                  onChange={(e) => setTypedAnswer(e.target.value)}
                  placeholder="Type the possessive form..."
                  className="text-center text-xl py-6"
                  disabled={showResult}
                  autoFocus
                />
                {!showResult && (
                  <Button type="submit" className="w-full" disabled={!typedAnswer.trim()}>
                    Check Answer
                  </Button>
                )}
              </form>
            )}

            {/* Result Feedback */}
            {showResult && (
              <div className={`mt-6 p-4 rounded-lg ${isCorrect ? 'bg-green-100' : 'bg-red-100'}`}>
                <div className="flex items-center justify-center gap-2 mb-2">
                  {isCorrect ? (
                    <>
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <span className="text-lg font-bold text-green-800">Correct!</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-6 h-6 text-red-600" />
                      <span className="text-lg font-bold text-red-800">Not quite!</span>
                    </>
                  )}
                </div>
                {!isCorrect && (
                  <p className="text-center text-gray-700">
                    The correct answer is: <strong className="text-green-700">{currentQuestion.correctAnswer}</strong>
                  </p>
                )}
                <div className="mt-4 text-center">
                  <Button onClick={nextQuestion} className="flex items-center gap-2 mx-auto">
                    {questionIndex + 1 >= totalQuestions ? 'See Results' : 'Next Question'}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quit Button */}
      <div className="text-center">
        <Button variant="ghost" onClick={resetGame} className="text-gray-500">
          Quit Game
        </Button>
      </div>
    </div>
  );
};

export default PossessivesGame;
